import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Heart, MessageCircle, Share2, Bookmark, Music, Volume2, VolumeX, ChevronUp, Search, X, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

let reelAudio = null;

export default function ReelsTab({ user, onProfileClick, showToast }) {
  const [reels, setReels] = useState([]);
  const [allReels, setAllReels] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [reelComments, setReelComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [savedReels, setSavedReels] = useState({});
  const [likersModal, setLikersModal] = useState(null);
  const [likersList, setLikersList] = useState([]);
  const containerRef = useRef(null);
  const touchStartY = useRef(0);
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };

  useEffect(() => { fetchReels(1); }, []);

  // Play sound when reel changes
  useEffect(() => {
    if (reels.length === 0 || isMuted) return;
    const reel = reels[currentIdx];
    if (!reel?.sound_id) { if (reelAudio) { reelAudio.pause(); reelAudio = null; } return; }
    if (reelAudio) reelAudio.pause();
    const audio = new Audio(`${API_URL}/api/sounds/file/${reel.sound_id}`);
    audio.loop = true;
    audio.volume = 0.4;
    audio.play().catch(() => {});
    reelAudio = audio;
    return () => { if (reelAudio) { reelAudio.pause(); reelAudio = null; } };
  }, [currentIdx, reels, isMuted]);

  // Cleanup on unmount
  useEffect(() => {
    return () => { if (reelAudio) { reelAudio.pause(); reelAudio = null; } };
  }, []);

  const fetchReels = async (pg) => {
    try {
      const res = await fetch(`${API_URL}/api/feed?page=${pg}&limit=20&type=reel`, { headers });
      if (res.ok) {
        const d = await res.json();
        const items = d.posts || [];
        if (pg === 1) { setAllReels(items); setReels(items); }
        else { setAllReels(prev => [...prev, ...items]); setReels(prev => [...prev, ...items]); }
        setHasMore(items.length >= 20);
      }
    } catch {}
    setLoading(false);
  };

  const loadMore = useCallback(() => {
    if (!hasMore) return;
    const next = page + 1;
    setPage(next);
    fetchReels(next);
  }, [page, hasMore]);

  const goNext = useCallback(() => {
    if (currentIdx < reels.length - 1) {
      setCurrentIdx(prev => prev + 1);
      if (currentIdx >= reels.length - 5) loadMore();
    }
  }, [currentIdx, reels.length, loadMore]);

  const goPrev = useCallback(() => {
    if (currentIdx > 0) setCurrentIdx(prev => prev - 1);
  }, [currentIdx]);

  const handleLike = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/api/posts/${postId}/like`, { method: 'POST', headers });
      if (res.ok) {
        const d = await res.json();
        setReels(prev => prev.map(r =>
          r.post_id === postId ? { ...r, likes_count: r.likes_count + (d.liked ? 1 : -1), user_liked: d.liked } : r
        ));
      }
    } catch {}
  };

  const handleComment = async (postId) => {
    setShowComments(true);
    try {
      const res = await fetch(`${API_URL}/api/posts/${postId}/comments`, { headers });
      if (res.ok) setReelComments(await res.json());
    } catch {}
  };

  const submitReelComment = async () => {
    if (!commentText.trim() || !reel) return;
    try {
      const res = await fetch(`${API_URL}/api/posts/${reel.post_id}/comment?text=${encodeURIComponent(commentText)}`, { method: 'POST', headers });
      if (res.ok) {
        setReels(prev => prev.map(r => r.post_id === reel.post_id ? { ...r, comments_count: (r.comments_count || 0) + 1 } : r));
        setCommentText('');
        const cRes = await fetch(`${API_URL}/api/posts/${reel.post_id}/comments`, { headers });
        if (cRes.ok) setReelComments(await cRes.json());
      }
    } catch {}
  };

  const handleShare = async (r) => {
    try {
      const url = `${window.location.origin}/app?post=${r.post_id}`;
      if (navigator.share) {
        navigator.share({ title: `${r.user_name} on Connect`, text: r.content?.substring(0, 100), url });
      } else {
        navigator.clipboard.writeText(url);
        if (showToast) showToast('Link copied!');
      }
    } catch {
      navigator.clipboard.writeText(`${window.location.origin}/app?post=${r?.post_id}`);
      if (showToast) showToast('Link copied!');
    }
  };

  const handleSave = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/api/posts/${postId}/save`, { method: 'POST', headers });
      if (res.ok) {
        const d = await res.json();
        setSavedReels(prev => ({ ...prev, [postId]: d.saved }));
        if (showToast) showToast(d.saved ? 'Saved!' : 'Unsaved');
      }
    } catch {}
  };

  const fetchLikers = async (postId) => {
    setLikersModal(postId);
    setLikersList([]);
    try {
      const res = await fetch(`${API_URL}/api/posts/${postId}/likers`, { headers });
      if (res.ok) setLikersList(await res.json());
    } catch {}
  };

  // Touch handlers for swipe
  const onTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
  const onTouchEnd = (e) => {
    const diff = touchStartY.current - e.changedTouches[0].clientY;
    if (Math.abs(diff) > 60) {
      if (diff > 0) goNext();
      else goPrev();
    }
  };

  // Wheel handler for desktop
  const wheelTimeout = useRef(null);
  const onWheel = (e) => {
    if (wheelTimeout.current) return;
    wheelTimeout.current = setTimeout(() => { wheelTimeout.current = null; }, 400);
    if (e.deltaY > 30) goNext();
    else if (e.deltaY < -30) goPrev();
  };

  // Keyboard
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowDown' || e.key === ' ') { e.preventDefault(); goNext(); }
      if (e.key === 'ArrowUp') { e.preventDefault(); goPrev(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goNext, goPrev]);

  // Search filter for reels
  useEffect(() => {
    if (!searchQuery.trim()) { setReels(allReels); return; }
    const q = searchQuery.toLowerCase();
    setReels(allReels.filter(r =>
      (r.content || '').toLowerCase().includes(q) ||
      (r.user_name || '').toLowerCase().includes(q)
    ));
    setCurrentIdx(0);
  }, [searchQuery, allReels]);

  if (loading) return (
    <div className="h-full flex items-center justify-center bg-black">
      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (reels.length === 0 && !searchQuery) return (
    <div className="h-full flex flex-col items-center justify-center bg-black text-zinc-500 px-6">
      <p className="text-sm text-center mb-4">No reels yet</p>
    </div>
  );

  const reel = reels[currentIdx];
  const mediaUrl = reel?.media_path?.startsWith('http') ? reel.media_path : (reel?.media_path ? `${API_URL}/api/storage/${reel.media_path}` : null);

  return (
    <div
      ref={containerRef}
      className="h-full bg-black relative select-none overflow-hidden"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onWheel={onWheel}
      data-testid="reels-tab"
    >
      {/* Full-screen reel background */}
      <div className="absolute inset-0">
        {mediaUrl ? (
          <img src={mediaUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full" style={{ backgroundColor: reel.bg_color || '#1a1a2e' }} />
        )}
        {/* Dark gradient overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/40" />
      </div>

      {/* Search bar overlay */}
      {showSearch && (
        <div className="absolute top-12 left-4 right-4 z-30" data-testid="reels-search-bar">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              type="text"
              autoFocus
              placeholder="Search reels..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-black/80 backdrop-blur-md border border-zinc-700 rounded-full py-2 pl-9 pr-9 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-500"
              data-testid="reels-search-input"
            />
            <button onClick={() => { setShowSearch(false); setSearchQuery(''); }} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="w-4 h-4 text-zinc-400" />
            </button>
          </div>
        </div>
      )}

      {/* Reel counter + mute + search */}
      <div className="absolute top-3 left-4 right-4 z-20 flex items-center justify-between">
        <span className="text-xs text-white/60 font-medium">{currentIdx + 1} / {reels.length}</span>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowSearch(!showSearch)} className="p-1.5 rounded-full bg-black/30 backdrop-blur-sm" data-testid="reels-search-toggle">
            <Search className="w-4 h-4 text-white/60" />
          </button>
          <button onClick={() => setIsMuted(!isMuted)} className="p-1.5 rounded-full bg-black/30 backdrop-blur-sm" data-testid="reel-mute-toggle">
            {isMuted ? <VolumeX className="w-4 h-4 text-white/60" /> : <Volume2 className="w-4 h-4 text-white" />}
          </button>
        </div>
      </div>

      {/* Text content overlay (for text posts or captions) */}
      {reel.post_type === 'text' && reel.content && (
        <div className="absolute inset-0 flex items-center justify-center z-10 px-10">
          <p className="text-white text-xl font-bold text-center leading-relaxed drop-shadow-lg">{reel.content}</p>
        </div>
      )}

      {/* Bottom info */}
      <div className="absolute bottom-6 left-4 right-20 z-20">
        <div className="flex items-center space-x-2.5 mb-2.5">
          <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/30 shrink-0 cursor-pointer"
            onClick={(e) => { e.stopPropagation(); onProfileClick?.(reel.user_id); }} data-testid={`reel-avatar-${currentIdx}`}>
            {reel.user_picture ? (
              <img src={reel.user_picture} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-zinc-700 flex items-center justify-center">
                <span className="text-xs font-bold">{reel.user_name?.[0]}</span>
              </div>
            )}
          </div>
          <div className="cursor-pointer" onClick={(e) => { e.stopPropagation(); onProfileClick?.(reel.user_id); }}>
            <p className="text-sm font-bold text-white hover:text-purple-300 transition-colors">{reel.user_name}</p>
            {reel.tagged_expertise?.length > 0 && (
              <p className="text-[11px] text-white/60">{reel.tagged_expertise[0]}</p>
            )}
          </div>
        </div>

        {reel.content && reel.media_path && (
          <p className="text-sm text-white/90 leading-snug line-clamp-3">{reel.content}</p>
        )}

        {reel.tagged_expertise?.length > 1 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {reel.tagged_expertise.slice(0, 4).map((t, i) => (
              <span key={i} className="text-[10px] text-white/50">#{t.replace(/\s/g, '')}</span>
            ))}
          </div>
        )}

        {/* Sound indicator */}
        {reel.sound_name && (
          <div className="flex items-center space-x-1.5 mt-2 bg-black/30 backdrop-blur-sm rounded-full px-3 py-1 w-fit">
            <Music className="w-3 h-3 text-white/70 animate-pulse" />
            <span className="text-[11px] text-white/70">{reel.sound_name}</span>
          </div>
        )}
      </div>

      {/* Right side actions */}
      <div className="absolute right-3 bottom-20 flex flex-col items-center space-y-6 z-20">
        {(reel.owner_privacy?.like !== false) && (
          <div className="flex flex-col items-center" data-testid={`reel-like-wrap-${currentIdx}`}>
            <button onClick={() => handleLike(reel.post_id)} data-testid={`reel-like-${currentIdx}`}>
              <Heart className={`w-8 h-8 ${reel.user_liked ? 'fill-red-500 text-red-500' : 'text-white'}`} />
            </button>
            <button onClick={() => fetchLikers(reel.post_id)}
              className="text-[11px] text-white mt-1 hover:underline" data-testid={`reel-likers-${currentIdx}`}>
              {reel.likes_count || 0}
            </button>
          </div>
        )}
        {(reel.owner_privacy?.comment !== false) && (
          <button onClick={() => handleComment(reel.post_id)} className="flex flex-col items-center" data-testid={`reel-comment-${currentIdx}`}>
            <MessageCircle className="w-7 h-7 text-white" />
            <span className="text-[11px] text-white mt-1">{reel.comments_count || 0}</span>
          </button>
        )}
        {(reel.owner_privacy?.share !== false) && (
          <button onClick={() => handleShare(reel)} className="flex flex-col items-center" data-testid={`reel-share-${currentIdx}`}>
            <Share2 className="w-7 h-7 text-white" />
          </button>
        )}
        {(reel.owner_privacy?.post_save !== false) && (
          <button onClick={() => handleSave(reel.post_id)} className="flex flex-col items-center" data-testid={`reel-save-${currentIdx}`}>
            <Bookmark className={`w-7 h-7 ${savedReels[reel.post_id] ? 'fill-white text-white' : 'text-white'}`} />
          </button>
        )}
      </div>

      {/* Comments overlay */}
      {showComments && (
        <div className="absolute inset-0 z-30 bg-black/70 flex items-end" onClick={() => setShowComments(false)} data-testid="reel-comments-overlay">
          <div className="w-full max-h-[60%] bg-zinc-900 rounded-t-2xl border-t border-zinc-700" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
              <h3 className="text-sm font-bold text-white">Comments ({reel.comments_count || 0})</h3>
              <button onClick={() => setShowComments(false)}><X className="w-5 h-5 text-zinc-400" /></button>
            </div>
            <div className="overflow-y-auto max-h-[40vh] px-4 py-2 space-y-2.5">
              {reelComments.length === 0 && <p className="text-xs text-zinc-600 py-4 text-center">No comments yet</p>}
              {reelComments.map((c, i) => (
                <div key={c.comment_id || i} className="flex items-start space-x-2">
                  <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center shrink-0 text-[10px] font-bold overflow-hidden cursor-pointer"
                    onClick={() => { setShowComments(false); onProfileClick?.(c.user_id); }}>
                    {c.user_picture ? <img src={c.user_picture} alt="" className="w-full h-full object-cover" /> : c.user_name?.[0]}
                  </div>
                  <div>
                    <p className="text-xs"><span className="font-semibold text-white">{c.user_name}</span> <span className="text-zinc-400">{c.text}</span></p>
                    <p className="text-[9px] text-zinc-600">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center space-x-2 px-4 py-3 border-t border-zinc-800">
              <div className="flex-1 space-y-1.5">
                <div className="flex gap-1 overflow-x-auto scrollbar-hide">
                  {['❤️','🔥','👏','😂','😍','🙌','💯','🎉','👀','🤔','💀','😭'].map(e => (
                    <button key={e} onClick={() => setCommentText(prev => prev + e)}
                      className="w-7 h-7 shrink-0 rounded-lg bg-zinc-700 hover:bg-zinc-600 flex items-center justify-center text-sm" data-testid={`reel-emoji-${e}`}>{e}</button>
                  ))}
                </div>
                <div className="flex items-center space-x-2">
                  <input value={commentText} onChange={e => setCommentText(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && submitReelComment()}
                    placeholder="Add a comment..." className="flex-1 bg-zinc-800 rounded-xl px-3 py-2 text-xs outline-none border border-zinc-700 text-white placeholder-zinc-600"
                    data-testid="reel-comment-input" />
                  <button onClick={submitReelComment} disabled={!commentText.trim()} className="text-white disabled:text-zinc-600" data-testid="reel-comment-submit">
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Likers Modal */}
      {likersModal && (
        <div className="absolute inset-0 z-40 bg-black/80 backdrop-blur-sm flex items-center justify-center" onClick={() => { setLikersModal(null); setLikersList([]); }} data-testid="reel-likers-modal">
          <div className="bg-zinc-900 w-full max-w-sm rounded-2xl border border-zinc-800 max-h-[70vh] overflow-hidden mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
              <h3 className="font-bold text-white text-sm">Liked by</h3>
              <button onClick={() => { setLikersModal(null); setLikersList([]); }}><X className="w-5 h-5 text-zinc-400" /></button>
            </div>
            <div className="overflow-y-auto max-h-[60vh]">
              {likersList.length === 0 ? (
                <p className="text-center text-zinc-500 text-xs py-8">No likes yet</p>
              ) : likersList.map(u => (
                <div key={u.user_id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-800 cursor-pointer"
                  onClick={() => { setLikersModal(null); onProfileClick?.(u.user_id); }}
                  data-testid={`reel-liker-${u.user_id}`}>
                  <div className="w-9 h-9 rounded-full bg-zinc-800 overflow-hidden shrink-0">
                    {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" /> :
                      <span className="w-full h-full flex items-center justify-center text-xs font-bold">{u.name?.[0]}</span>}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{u.name}</p>
                    <p className="text-[10px] text-zinc-500">@{u.username}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Swipe hint for first reel */}
      {currentIdx === 0 && (
        <div className="absolute bottom-1/3 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <ChevronUp className="w-6 h-6 text-white/30" />
          <p className="text-[10px] text-white/30 text-center">Swipe up</p>
        </div>
      )}
    </div>
  );
}
